/**
 * Publicar um plugin oficial (docs/spec/web-plugins.md, "Plugins oficiais"): o pacote vem do Desk
 * Cloud de quem publica ou do computador (as mesmas abas de "Instalar plugin…"), é conferido aqui e a
 * confirmação mostra o resumo, a troca de versão (ou por que não dá para publicar) e os sistemas para
 * que ele serve. "Enviar versão nova" é o mesmo diálogo, preso a um oficial. Também o diálogo dos
 * sistemas de um oficial.
 */
import { useId, useState } from 'react';
import { api } from '../api.js';
import { useLanguage, useT } from '../i18n/index.js';
import { cloudFileBytes, inspectPluginBytes } from '../plugins/web/install.js';
import { CloudPluginPanel, ComputerPluginPanel, DialogTabs, PluginSummary, inspectionReport } from '../plugins/web/PluginParts.js';
import { compareVersions, localize, type Inspection } from '../plugins/web/sdk.js';
import { cleanSystems, OFFICIAL_PLUGIN_LIMITS, systemMatchKey, type AdminOfficialPlugin, type DeskFile, type KnownSystem } from '../shared.js';
import { describeError, useApp } from '../store.js';
import { Button, CheckBox, Dialog, TextEdit } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { toast } from '../ui/Toaster.js';

type Source = 'cloud' | 'computer';
type Read = { fileName: string; inspection: Inspection };

/** Quantos sistemas das mesas o campo sugere de uma vez. */
const SUGGESTIONS = 8;

/**
 * Os sistemas de um plugin oficial: as etiquetas, o campo (Enter ou vírgula acrescentam) e, embaixo,
 * os sistemas que as mesas mais usam, escritos como elas escrevem (é com eles que a aba Oficiais casa).
 */
export function SystemsField({ value, onChange, known }: { value: string[]; onChange: (systems: string[]) => void; known: KnownSystem[] }) {
  const t = useT();
  const id = useId();
  const [text, setText] = useState('');
  const full = value.length >= OFFICIAL_PLUGIN_LIMITS.systems;
  const add = (raw: string) => {
    if (!raw.trim()) return;
    onChange(cleanSystems([...value, raw]));
    setText('');
  };
  const taken = new Set(value.map(systemMatchKey));
  const suggestions = known.filter((k) => !taken.has(systemMatchKey(k.name))).slice(0, SUGGESTIONS);
  return (
    <div className="rd-systemsField" role="group" aria-labelledby={`${id}-label`}>
      <label className="rd-systemsField__label" id={`${id}-label`} htmlFor={`${id}-input`}>
        {t('admin.plugins.systemsLabel')}
      </label>
      <p className="rd-systemsField__hint">{t('admin.plugins.systemsHint')}</p>
      {value.length ? (
        <ul className="rd-systemsField__tags" aria-label={t('admin.plugins.systemsLabel')}>
          {value.map((system) => (
            <li key={system} className="rd-pluginTag rd-systemsField__tag">
              {system}
              <button type="button" className="rd-systemsField__remove" aria-label={t('admin.plugins.removeSystem', { system })} onClick={() => onChange(value.filter((s) => s !== system))}>
                <Icon name="close" size={12} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="rd-systemsField__add">
        <TextEdit
          id={`${id}-input`}
          value={text}
          onChange={setText}
          maxLength={OFFICIAL_PLUGIN_LIMITS.system}
          disabled={full}
          placeholder={t(full ? 'admin.plugins.systemsFull' : 'admin.plugins.systemPlaceholder')}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ',') return;
            e.preventDefault();
            add(text);
          }}
        />
        <Button variant="secondary" disabled={!text.trim() || full} onClick={() => add(text)}>
          {t('admin.plugins.addSystem')}
        </Button>
      </div>
      {suggestions.length && !full ? (
        <div className="rd-systemsField__suggest">
          <span className="rd-systemsField__suggestLabel">{t('admin.plugins.roomSystems')}</span>
          {suggestions.map((k) => (
            <button key={k.name} type="button" className="rd-systemsField__chip" aria-label={t('admin.plugins.addKnown', { system: k.name, n: k.rooms })} onClick={() => add(k.name)}>
              <Icon name="plus" size={12} />
              {k.name}
              <span className="rd-systemsField__count">{k.rooms}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Publicar: as abas de origem e, com o pacote lido, a confirmação. `target`: "Enviar versão nova" de
 * um oficial (o pacote tem de ser dele); sem ele, um pacote com o `id` de um oficial que já existe vira
 * a versão nova dele, como no servidor.
 */
export function AdminPluginDialog({ target, plugins, knownSystems, onDone }: { target: AdminOfficialPlugin | null; plugins: AdminOfficialPlugin[]; knownSystems: KnownSystem[]; onDone: (changed: boolean) => void }) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const token = useApp((s) => s.token);
  const [source, setSource] = useState<Source>('computer');
  const [cloudChoice, setCloudChoice] = useState<DeskFile | null>(null);
  const [reading, setReading] = useState(false);
  const [read, setRead] = useState<Read | null>(null);
  const [systems, setSystems] = useState<string[]>(target?.systems ?? []);
  const [visible, setVisible] = useState(true);
  const [busy, setBusy] = useState(false);

  const title = target ? t('admin.plugins.newVersionTitle', { name: localize(target.name, language) }) : t('admin.plugins.addTitle');
  const m = read?.inspection.manifest ?? null;
  // a versão nova é do oficial escolhido ou do que já tem o mesmo `id` de manifesto
  const existing = target ?? (m ? plugins.find((p) => p.moduleId === m.id) : undefined) ?? null;
  const order = m && existing ? compareVersions(m.version, existing.version) : 1;
  const otherPlugin = !!(target && m && m.id !== target.moduleId);
  const refusal = otherPlugin
    ? t('admin.plugins.otherPlugin', { id: target!.moduleId })
    : existing && order === 0
      ? t('admin.plugins.sameVersion', { version: existing.version })
      : existing && order < 0
        ? t('admin.plugins.older', { version: existing.version })
        : null;
  const errors = read ? read.inspection.issues.filter((i) => i.level === 'error').length : 0;
  const canPublish = !!read?.inspection.pkg && !errors && !refusal;

  /** Lê e confere o pacote; com ele, os sistemas começam pelos do oficial que ele atualiza. */
  const load = async (task: () => Promise<Read>) => {
    setReading(true);
    try {
      const next = await task();
      const manifestId = next.inspection.manifest?.id;
      const updates = target ?? plugins.find((p) => p.moduleId === manifestId);
      if (updates) setSystems(updates.systems);
      setRead(next);
    } catch (failure) {
      toast(describeError(failure), 'error');
    } finally {
      setReading(false);
    }
  };
  const fromCloud = (file: DeskFile) => void load(async () => ({ fileName: file.name, inspection: inspectPluginBytes(file.name, await cloudFileBytes(file)) }));
  const fromComputer = (file: File) => void load(async () => ({ fileName: file.name, inspection: inspectPluginBytes(file.name, new Uint8Array(await file.arrayBuffer())) }));

  const publish = async () => {
    const pkg = read?.inspection.pkg;
    if (!token || !pkg || !canPublish) return;
    setBusy(true);
    try {
      const result = await api.adminPublishPlugin(token, pkg.zip, {
        ...(target ? { officialId: target.id } : {}),
        systems,
        ...(existing ? {} : { hidden: !visible }),
      });
      const name = localize(result.plugin.name, language);
      toast(result.from ? t('admin.plugins.versionDone', { name, version: result.plugin.version }) : t('admin.plugins.addDone', { name }), 'success');
      onDone(true);
    } catch (failure) {
      setBusy(false);
      toast(describeError(failure), 'error');
    }
  };

  const notice = !m ? null : refusal ? (
    <p className="rd-webinstall__warn" role="alert">
      {refusal}
    </p>
  ) : existing ? (
    // com o título "Versão nova de …", o aviso diz só a troca; adicionando, diz de qual plugin é
    <p className="rd-webinstall__hint">
      {target ? t('admin.plugins.versionChange', { from: existing.version, to: m.version }) : t('admin.plugins.versionUp', { name: localize(existing.name, language), from: existing.version, to: m.version })}
    </p>
  ) : (
    <p className="rd-webinstall__hint">{t('admin.plugins.isNew')}</p>
  );

  const actions = read ? (
    <>
      <Button variant="ghost" className="rd-pluginInstall__back" icon="chevronLeft" disabled={busy} onClick={() => setRead(null)}>
        {t('common.back')}
      </Button>
      {read.inspection.issues.length ? (
        <Button variant="secondary" onClick={() => void navigator.clipboard.writeText(inspectionReport(read.inspection, read.fileName, language)).then(() => toast(t('webInstall.copied'), 'success'))}>
          {t('webInstall.copy')}
        </Button>
      ) : null}
      <Button variant="secondary" disabled={busy} onClick={() => onDone(false)}>
        {t('create.cancel')}
      </Button>
      <Button variant="action" busy={busy} disabled={!canPublish} onClick={() => void publish()}>
        {t('admin.plugins.publish')}
      </Button>
    </>
  ) : (
    <>
      <Button variant="secondary" onClick={() => onDone(false)}>
        {t('create.cancel')}
      </Button>
      {source === 'cloud' ? (
        <Button variant="action" busy={reading} disabled={!cloudChoice} onClick={() => cloudChoice && fromCloud(cloudChoice)}>
          {t('pluginInstall.cloud.continue')}
        </Button>
      ) : null}
    </>
  );

  return (
    <Dialog title={title} onClose={() => !busy && onDone(false)} wide className="rd-pluginInstall" actions={actions}>
      {read ? (
        <PluginSummary inspection={read.inspection} fileName={read.fileName} notice={notice}>
          {m && !otherPlugin ? (
            <div className="rd-adminPluginForm">
              <SystemsField value={systems} onChange={setSystems} known={knownSystems} />
              {existing ? null : (
                <CheckBox checked={visible} onChange={setVisible}>
                  {t('admin.plugins.visibleNow')}
                </CheckBox>
              )}
            </div>
          ) : null}
        </PluginSummary>
      ) : null}
      <div className="rd-pluginInstall__sources" hidden={!!read}>
        <DialogTabs
          tabs={[
            { key: 'computer', label: t('pluginInstall.tab.computer') },
            { key: 'cloud', label: t('pluginInstall.tab.cloud') },
          ]}
          value={source}
          onChange={setSource}
          label={t('pluginInstall.tabs')}
          idBase="rd-official-publish"
        />
        <div className="rd-pluginInstall__panel" role="tabpanel" id={`rd-official-publish-${source}`} aria-labelledby={`rd-official-publish-tab-${source}`}>
          {source === 'computer' ? <ComputerPluginPanel busy={reading} onFile={fromComputer} hint={t('admin.plugins.computerHint')} /> : null}
          {source === 'cloud' ? <CloudPluginPanel chosen={cloudChoice} onChoose={setCloudChoice} onPick={fromCloud} hint={t('admin.plugins.cloudHint')} /> : null}
        </div>
      </div>
    </Dialog>
  );
}

/** Os sistemas de um oficial: o mesmo campo do cadastro, num diálogo próprio. */
export function AdminPluginSystemsDialog({ target, knownSystems, onDone }: { target: AdminOfficialPlugin; knownSystems: KnownSystem[]; onDone: (changed: boolean) => void }) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const token = useApp((s) => s.token);
  const [systems, setSystems] = useState<string[]>(target.systems);
  const [busy, setBusy] = useState(false);
  const unchanged = systems.length === target.systems.length && systems.every((s, i) => s === target.systems[i]);
  const name = localize(target.name, language);

  const save = async () => {
    if (!token) return;
    setBusy(true);
    try {
      await api.adminPatchPlugin(token, target.id, { systems });
      toast(t('admin.plugins.systemsDone', { name }), 'success');
      onDone(true);
    } catch (failure) {
      setBusy(false);
      toast(describeError(failure), 'error');
    }
  };

  return (
    <Dialog
      title={t('admin.plugins.systemsTitle', { name })}
      onClose={() => !busy && onDone(false)}
      actions={
        <>
          <Button variant="secondary" disabled={busy} onClick={() => onDone(false)}>
            {t('create.cancel')}
          </Button>
          <Button variant="action" busy={busy} disabled={unchanged} onClick={() => void save()}>
            {t('admin.plugins.save')}
          </Button>
        </>
      }
    >
      <SystemsField value={systems} onChange={setSystems} known={knownSystems} />
    </Dialog>
  );
}
